import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import DashboardPage from "../../components/organisms/DashboardPage/DashboardPage";
import EmployeesPage from "../../components/organisms/EmployeesPage/EmployeesPage";
import AssignedProjectsPage from "../../components/organisms/AssignedProjectsPage/AssignedProjectsPage";
import DailyHoursPage from "../../components/organisms/DailyHoursPage/DailyHoursPage";
import LeavePage from "../../components/organisms/LeavePage/LeavePage";
import ApplyLeavedrawer from "../../components/molecules/Apply-Leave-Drawer/Apply-Leave-drawer";
import ApplyHoursdrawer from "../../components/molecules/Apply-Hours-Drawer/Apply-Hours-Drawer";
import LoginPage from "../../components/organisms/LoginPage/LoginPage";
import WithAuth from "../utils/common/ProtectedRoute";
import BlogForm from "../../components/organisms/blogForm";
import AllRolePage from "../../components/organisms/AllRolePage/AllRolePage";
import LeaveApprove from "../../components/organisms/LeaveApprover/LeaveApprove";
import ProfilePage from "../../components/organisms/ProfilePage/ProfilePage";
import TaskFilter from "../../components/organisms/TaskFilterPage/TaskFilter";
import AttendancePage from "../../components/organisms/AttendancePage/AttendancePage";
import EmployeeDirectoryPage from "../../components/organisms/EmployeeDirectoryPage/EmployeeDirectoryPage";
import TeamPraisePage from "../../components/organisms/TeamPraisePage/TeamPraisePage";
import ProjectDocumentationPage from "../../components/organisms/ProjectDocumentationPage/ProjectDocumentationPage";
import CompanyPolicyPage from "../../components/organisms/CompanyPolicyPage/CompanyPolicyPage";
import CompanyDetailsPage from "../../components/organisms/CompanyDetailsPage/CompanyDetailsPage";
import ClientManagementPage from "../../components/organisms/ClientManagementPage/ClientManagementPage";
import InvoiceManagementPage from "../../components/organisms/InvoiceManagementPage/InvoiceManagementPage";
import FinanceManagementPage from "../../components/organisms/FinanceManagementPage/FinanceManagementPage";
import SalarySlipPage from "../../components/organisms/SalarySlipPage/SalarySlipPage";
import CompanyOpsPage from "../../components/organisms/CompanyOpsPage/CompanyOpsPage";
import ProjectBoardDashboardPage from "../../components/organisms/ProjectBoardDashboardPage/ProjectBoardDashboardPage";
import ProjectBoardPage from "../../components/organisms/ProjectBoardPage/ProjectBoardPage";
import SopsPage from "../../components/organisms/SopsPage/SopsPage";
import OffboardingDocumentsPage from "../../components/organisms/OffboardingDocumentsPage/OffboardingDocumentsPage";
import ProjectCredentialsPage from "../../components/organisms/ProjectCredentialsPage/ProjectCredentialsPage";
import PerformanceCriteriaPage from "../../components/organisms/PerformanceCriteriaPage/PerformanceCriteriaPage";
import TimeTrackerPage from "../../components/organisms/TimeTrackerPage/TimeTrackerPage";
import TimeTrackerAdminPage from "../../components/organisms/TimeTrackerAdminPage/TimeTrackerAdminPage";

// HOCs must be created once; recreating them inside AppRoutes remounts
// the page on every auth state change and re-fires getUserProfile.
const ProtectedDashboard = WithAuth(DashboardPage);
const ProtectedEmployee = WithAuth(EmployeesPage);
const ProtectedProjects = WithAuth(AssignedProjectsPage);
const ProtectedDailyHours = WithAuth(DailyHoursPage);
const ProtectedLeaveRequest = WithAuth(LeavePage);
const ProtectedRoles = WithAuth(AllRolePage);
const ProtectedLeaveApprove = WithAuth(LeaveApprove);
const ProtectedProfile = WithAuth(ProfilePage);
const ProtectedTaskFilter = WithAuth(TaskFilter);
const ProtectedAttendance = WithAuth(AttendancePage);
const ProtectedEmployeeDirectory = WithAuth(EmployeeDirectoryPage);
const ProtectedTeamPraise = WithAuth(TeamPraisePage);
const ProtectedProjectDocumentation = WithAuth(ProjectDocumentationPage);
const ProtectedCompanyPolicy = WithAuth(CompanyPolicyPage);
const ProtectedCompanyDetails = WithAuth(CompanyDetailsPage);
const ProtectedClientManagement = WithAuth(ClientManagementPage);
const ProtectedInvoiceManagement = WithAuth(InvoiceManagementPage);
const ProtectedFinanceManagement = WithAuth(FinanceManagementPage);
const ProtectedSalarySlips = WithAuth(SalarySlipPage);
const ProtectedCompanyOps = WithAuth(CompanyOpsPage);
const ProtectedProjectManagement = WithAuth(ProjectBoardDashboardPage);
const ProtectedProjectBoard = WithAuth(ProjectBoardPage);
const ProtectedSops = WithAuth(SopsPage);
const ProtectedOffboardingDocuments = WithAuth(OffboardingDocumentsPage);
const ProtectedProjectCredentials = WithAuth(ProjectCredentialsPage);
const ProtectedPerformanceCriteria = WithAuth(PerformanceCriteriaPage);
const ProtectedTimeTracker = WithAuth(TimeTrackerPage);
const ProtectedTimeTrackerAdmin = WithAuth(TimeTrackerAdminPage);

const AppRoutes = () => {
  const location = useLocation();
  const [leaveDrawerOpen, setLeaveDrawerOpen] = useState(false);
  const [leaveToEdit, setLeaveToEdit] = useState(null);
  const [leaveDraft, setLeaveDraft] = useState(null);
  const [hoursDrawerOpen, setHoursDrawerOpen] = useState(false);
  const [hoursSelectedDate, setHoursSelectedDate] = useState(() => new Date());

  const toggleLeaveDrawer = useCallback(() => {
    setLeaveDrawerOpen((o) => !o);
  }, []);

  useEffect(() => {
    if (!leaveDrawerOpen) {
      setLeaveToEdit(null);
      setLeaveDraft(null);
    }
  }, [leaveDrawerOpen]);

  const toggleHoursDrawer = useCallback(() => {
    setHoursDrawerOpen((o) => !o);
  }, []);

  const openLeaveDrawer = useCallback((draft = null) => {
    setLeaveToEdit(null);
    setLeaveDraft(draft || null);
    setLeaveDrawerOpen(true);
  }, []);

  const openLeaveDrawerForEdit = useCallback((leave) => {
    setLeaveToEdit(leave || null);
    setLeaveDrawerOpen(true);
  }, []);

  const openHoursDrawer = useCallback(() => {
    setHoursSelectedDate(new Date());
    setHoursDrawerOpen(true);
  }, []);

  useEffect(() => {
    if (location.pathname === "/daily-hours") {
      setHoursDrawerOpen(false);
    }
  }, [location.pathname]);

  const showGlobalHoursDrawer = location.pathname !== "/daily-hours";
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedDashboard
              openLeaveDrawer={openLeaveDrawer}
              openHoursDrawer={openHoursDrawer}
            />
          }
        />
        <Route path="/employees" element={<ProtectedEmployee />} />
        <Route path="/employee-directory" element={<ProtectedEmployeeDirectory />} />
        <Route path="/assigned-projects" element={<ProtectedProjects />} />
        <Route path="/project-management" element={<ProtectedProjectManagement />} />
        <Route path="/project-management/:boardId" element={<ProtectedProjectBoard />} />
        <Route path="/project-documentation" element={<ProtectedProjectDocumentation />} />
        <Route path="/company-details" element={<ProtectedCompanyDetails />} />
        <Route path="/client-management" element={<ProtectedClientManagement />} />
        <Route path="/invoice-management" element={<ProtectedInvoiceManagement />} />
        <Route path="/finance-management" element={<Navigate to="/finance-management/sheets" replace />} />
        <Route
          path="/finance-management/documents"
          element={<Navigate to="/finance-management/expenses/documents" replace />}
        />
        <Route path="/finance-management/expenses/:expenseSection" element={<ProtectedFinanceManagement />} />
        <Route path="/finance-management/:tab" element={<ProtectedFinanceManagement />} />
        <Route path="/salary-slips" element={<ProtectedSalarySlips />} />
        <Route path="/my-salary-slips" element={<ProtectedSalarySlips />} />
        <Route path="/company-ops" element={<ProtectedCompanyOps />} />
        <Route path="/company-policy" element={<ProtectedCompanyPolicy />} />
        <Route path="/daily-hours" element={<ProtectedDailyHours />} />
        <Route
          path="/leave_request"
          element={
            <ProtectedLeaveRequest
              openLeaveDrawer={openLeaveDrawer}
              openWorkFromHomeDrawer={() => openLeaveDrawer({ workLocation: "wfh" })}
              onEditLeaveRequest={openLeaveDrawerForEdit}
            />
          }
        />
        <Route
          path="/roles"
          element={<ProtectedRoles />}
        />
        <Route path="/sops" element={<ProtectedSops />} />
        <Route path="/offboarding-documents" element={<ProtectedOffboardingDocuments />} />
        <Route path="/project-credentials" element={<ProtectedProjectCredentials />} />
        <Route path="/performance-criteria" element={<ProtectedPerformanceCriteria />} />
        <Route path="/time-tracker" element={<ProtectedTimeTracker />} />
        <Route path="/time-tracker/admin" element={<ProtectedTimeTrackerAdmin />} />
        <Route path="/leave_approve" element={<Navigate to="/leave_approve/requests" replace />} />
        <Route path="/leave_approve/requests" element={<ProtectedLeaveApprove />} />
        <Route path="/leave_approve/balances" element={<ProtectedLeaveApprove />} />
        <Route path="/leave_approve/ex-balances" element={<ProtectedLeaveApprove />} />
        <Route
          path="/profile"
          element={<ProtectedProfile />}
        />
        <Route
          path="/task_filter"
          element={<ProtectedTaskFilter />}
        />
        <Route path="/attendance" element={<ProtectedAttendance />} />
        <Route path="/attendance/history" element={<ProtectedAttendance />} />
        <Route path="/attendance/team" element={<ProtectedAttendance />} />
        <Route path="/attendance/integration" element={<ProtectedAttendance />} />
        <Route path="/team-praise" element={<ProtectedTeamPraise />} />
        <Route
          path="*"
          element={<Navigate to="/login" />}
        />

      </Routes>
      <ApplyLeavedrawer
        isOpen={leaveDrawerOpen}
        toggleLeaveDrawer={toggleLeaveDrawer}
        editingLeave={leaveToEdit}
        initialValues={leaveDraft}
      />
      {showGlobalHoursDrawer ? (
        <ApplyHoursdrawer
          isOpen={hoursDrawerOpen}
          toggleHoursDrawer={toggleHoursDrawer}
          selectedDate={hoursSelectedDate}
        />
      ) : null}
    </>
  );
};

export default AppRoutes;
